import React, { useState } from 'react';

import { List, Datagrid, TextField, DeleteButton, Edit, TextInput, Button, Create, SimpleForm, TopToolbar, FormTab, TabbedForm, FormDataConsumer, required, ResourceContextProvider, useCreate, useRefresh, FormWithRedirect, Toolbar, SaveButton, useNotify, useRedirect, DateInput, DateField, NumberInput, NumberField, ReferenceInput, SelectInput, FunctionField } from 'react-admin';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconContentAdd from '@material-ui/icons/Add';
import IconCancel from '@material-ui/icons/Cancel';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { Link } from 'react-router-dom';

import { useTranslate } from '../common/language';
import { validateMaxLength } from './../components/Validator';

const isFeaturedTopic = record => record && record.topic_friendly_url === 'featured';
const parseNullableDate = value => value || null;

export const EventsTopicsItemsList = props => {
  const translate = useTranslate();

  return (
    <ResourceContextProvider value="events_topics_items">
      <List
        basePath="/events_topics_items"
        filter={{ events_topics_id: props.events_topics_id, no_pagination: true, no_sort: true }}
        actions={<ListActions events_topics_id={props.events_topics_id} />}
        empty={<ListActions events_topics_id={props.events_topics_id} />}
        bulkActionButtons={false}
        perPage={1000000}
        pagination={false}
        sort={{ field: 'id', order: 'ASC' }}
        label=""
        title=""
        resource="events_topics_items"
      >
        <Datagrid rowClick="edit">
          <TextField source="event_name" label={translate('events_topics_items.event')} sortable={false} />
          <TextField source="association_name" label={translate('events_topics_items.association')} sortable={false} />
          <DateField source="date_start" label={translate('events_topics_items.date_start')} sortable={false} />
          <DateField source="date_finish" label={translate('events_topics_items.date_finish')} sortable={false} />
          <TextField source="label" label={translate('events_topics_items.label')} sortable={false} />
          <NumberField source="order" label={translate('events_topics_items.order')} sortable={false} />
          <FunctionField label="" render={record => <EventTopicItemDeleteButton record={record} />} sortable={false} />
        </Datagrid>
      </List>
    </ResourceContextProvider>
  );
};

const EventTopicItemDeleteButton = ({ record }) => {
  const redirect = record && record.events_topics_id ? `/events_topics/${record.events_topics_id}/events` : '/events_topics';

  return <DeleteButton undoable={false} redirect={redirect} label="" />;
};

const ListActions = props => (
  <TopToolbar>
    <MyCreateButton events_topics_id={props.events_topics_id} />
  </TopToolbar>
);

export const MyCreateButton = ({ events_topics_id }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [create, { loading }] = useCreate('events_topics_items');
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const handleSubmit = values => {
    create(
      { payload: { data: values } },
      {
        onSuccess: () => {
          setShowDialog(false);
          refresh();
        },
        onFailure: error => {
          notify(error.message, 'error');
        }
      }
    );
  };

  return (
    <>
      <Button onClick={() => setShowDialog(true)} label={translate('events_topics_items.create')}>
        <IconContentAdd />
      </Button>

      <Dialog fullWidth open={showDialog} onClose={() => setShowDialog(false)} aria-label={translate('events_topics_items.create')}>
        <DialogTitle>{translate('events_topics_items.create')}</DialogTitle>

        <FormWithRedirect
          save={handleSubmit}
          initialValues={{ events_topics_id, order: 0 }}
          render={({ handleSubmitWithRedirect, pristine, saving }) => (
            <>
              <DialogContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <EventInput />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DateInput fullWidth source="date_start" label={translate('events_topics_items.date_start')} parse={parseNullableDate} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DateInput fullWidth source="date_finish" label={translate('events_topics_items.date_finish')} parse={parseNullableDate} />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextInput fullWidth source="label" label={translate('events_topics_items.label')} validate={[validateMaxLength(255, translate('common.validation.maxLength'))]} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <NumberInput fullWidth source="order" label={translate('events_topics_items.order')} />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button label="ra.action.cancel" onClick={() => setShowDialog(false)} disabled={loading}>
                  <IconCancel />
                </Button>
                <SaveButton handleSubmitWithRedirect={handleSubmitWithRedirect} pristine={pristine} saving={saving} disabled={loading} />
              </DialogActions>
            </>
          )}
        />
      </Dialog>
    </>
  );
};

const EventInput = props => {
  const translate = useTranslate();

  return (
    <ReferenceInput
      fullWidth
      source="events_id"
      reference="events_topics_items/events"
      perPage={1000}
      sort={{ field: 'id', order: 'ASC' }}
      filter={{ q: '' }}
      label={translate('events_topics_items.event')}
      validate={[required()]}
      {...props}
    >
      <SelectInput optionText="name" optionValue="id" />
    </ReferenceInput>
  );
};

const MyActions = ({ data }) => {
  const translate = useTranslate();
  const linkBack = data && data.events_topics_id ? `/events_topics/${data.events_topics_id}/events` : '/events_topics';

  return (
    <TopToolbar style={{ justifyContent: 'flex-start' }}>
      <Link to={linkBack}>
        <ArrowBackIosNewIcon className="mr-2" />
      </Link>
      <Typography variant="h5" className="grey8" align="left" style={{ textAlign: 'left', flex: 1 }}>
        {data && data.event_name ? data.event_name : translate('events_topics_items.title')}
      </Typography>
    </TopToolbar>
  );
};

export const EventsTopicsItemsEdit = ({ id, ...props }) => {
  const translate = useTranslate();
  const classes = useStyles();
  const notify = useNotify();
  const redirect = useRedirect();

  const onSuccess = ({ data }) => {
    notify(translate('common.notification.updated'));
    redirect(`/events_topics/${data.events_topics_id}/events`);
  };

  return (
    <Edit
      title={<ElementTitle {...props} />}
      {...props}
      id={id}
      mutationMode="pessimistic"
      actions={<MyActions {...props} />}
      onSuccess={onSuccess}
    >
      <TabbedForm toolbar={<FormToolbar />}>
        <FormTab label={translate('classrooms.data')} contentClassName={classes.tab} path="">
          <FormDataConsumer>
            {({ formData }) => (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {isFeaturedTopic(formData) ? (
                    <TextInput fullWidth source="event_name" label={translate('events_topics_items.event')} disabled />
                  ) : (
                    <EventInput />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateInput fullWidth source="date_start" label={translate('events_topics_items.date_start')} parse={parseNullableDate} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateInput fullWidth source="date_finish" label={translate('events_topics_items.date_finish')} parse={parseNullableDate} />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextInput fullWidth source="label" label={translate('events_topics_items.label')} validate={[validateMaxLength(255, translate('common.validation.maxLength'))]} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NumberInput fullWidth source="order" label={translate('events_topics_items.order')} />
                </Grid>
              </Grid>
            )}
          </FormDataConsumer>
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};

const ElementTitle = ({ record }) => {
  const translate = useTranslate();
  return <span>{translate('common.edit')} {record ? `"${record.event_name || record.events_id}"` : ''}</span>;
};

const FormToolbar = props => {
  const record = props.record;
  const redirect = record && record.events_topics_id ? `/events_topics/${record.events_topics_id}/events` : '/events_topics';

  return (
    <Toolbar {...props} className="user_form_toolbar">
      <SaveButton />
      {!isFeaturedTopic(record) ? <DeleteButton redirect={redirect} mutationMode="pessimistic" /> : null}
    </Toolbar>
  );
};

const useStyles = makeStyles({
  tab: {
    maxWidth: '100%',
    display: 'block',
  },
});

export const EventsTopicsItemsCreate = props => {
  const translate = useTranslate();

  return (
    <Create {...props}>
      <SimpleForm redirect="list" initialValues={{ order: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" className="grey">{translate('events_topics_items.create')}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ReferenceInput fullWidth source="events_topics_id" reference="events_topics" perPage={1000} sort={{ field: 'id', order: 'ASC' }} validate={[required()]}>
              <SelectInput optionText="name" optionValue="id" />
            </ReferenceInput>
          </Grid>
          <Grid item xs={12} sm={6}>
            <EventInput />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateInput fullWidth source="date_start" label={translate('events_topics_items.date_start')} parse={parseNullableDate} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateInput fullWidth source="date_finish" label={translate('events_topics_items.date_finish')} parse={parseNullableDate} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextInput fullWidth source="label" label={translate('events_topics_items.label')} validate={[validateMaxLength(255, translate('common.validation.maxLength'))]} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <NumberInput fullWidth source="order" label={translate('events_topics_items.order')} />
          </Grid>
        </Grid>
      </SimpleForm>
    </Create>
  );
};
